import React, { Fragment, useCallback, useEffect, useState } from "react";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import { allTrue, isBlank, notBlank } from "../../../util/ext";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Skeleton } from "@mui/material";
import { useOptIns, useUpdateOptIn } from "../../cspServices";
import {
  any,
  equals,
  find,
  lensProp,
  mergeRight,
  pick,
  pickBy,
  propEq,
  reject,
  set,
} from "ramda";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import OptInForm, { validateOptIn, validateOptInField } from "./OptInForm";
import ErrorMessage from "../../../components/alert/ErrorMessage";
import LoadingIndicator from "../../../components/LoadingIndicator";
import { caseInsensitiveSort } from "../../../util/sorts";

const EDITABLE_FIELDS = [
  "optInName",
  "optInTitle",
  "optInDescription",
  "acceptTitle",
  "acceptDescription",
  "declineTitle",
  "declineDescription",
  "regional",
  "termsUri",
  "logoUri",
];

const ProjectOptInDialog = ({ project, optIn, open, onCancel, onApply }) => {
  const [selectedOptIn, setSelectedOptIn] = useState(null);
  const [fields, setFields] = useState({
    optInId: null,
    preSubmission: false,
    required: false,
  });
  const [optIns, setOptIns] = useState([]);

  const optInsResponse = useOptIns();
  useEffect(() => {
    if (optInsResponse.isSuccess && notBlank(optInsResponse.data)) {
      if (notBlank(optIn)) {
        setFields(pick(["optInId", "preSubmission", "required"], optIn));
        setOptIns(optInsResponse.data);
      } else {
        const unused = reject(
          opt => any(propEq("optInId", opt.optInId), project.optIns),
          optInsResponse.data,
        );
        setOptIns(unused);
      }
    }
  }, [optInsResponse.isSuccess, optInsResponse.data, optIn, project.optIns]);

  const [editableFields, setEditableFields] = useState(null);
  useEffect(() => {
    if (notBlank(optIns) && notBlank(fields.optInId)) {
      const selected = find(propEq("optInId", fields.optInId), optIns);
      setSelectedOptIn(selected);
      setEditableFields(pick(EDITABLE_FIELDS, selected));
    } else {
      setSelectedOptIn(null);
    }
  }, [fields.optInId, optIns]);

  /** Opt-In Update */
  const [errors, setErrors] = useState({});
  const handleChange = prop => event => {
    const value = event.target.value;
    setEditableFields(set(lensProp(prop), value));
  };

  const handleCheckbox = field => event => {
    const value = event.target.checked;
    setEditableFields(set(lensProp(field), value));
  };

  const handleBlur = (prop, value) => () =>
    setErrors(validateOptInField(prop, value));
  /** End Opt-In Update */

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleOptInChange = useCallback((_event, value) => {
    setFields(prev => set(lensProp("optInId"), value.optInId, prev));
  }, []);

  const updateOptIn = useUpdateOptIn();
  const objectDiff = optIn => (val, key) => !equals(optIn[key], val);
  const handleApply = async () => {
    try {
      if (notBlank(selectedOptIn)) {
        // check for diff
        const initialOptIn = pick(EDITABLE_FIELDS, selectedOptIn);
        const update = pickBy(objectDiff(initialOptIn), editableFields);

        if (notBlank(update)) {
          const validation = validateOptIn(editableFields);
          if (notBlank(validation)) {
            setErrors(validation);
            throw Error("Please correct issues before saving");
          }

          const data = pickBy(notBlank, {
            ...editableFields,
            optInId: selectedOptIn.optInId,
            optInProjectId: selectedOptIn.optInProjectId,
          });

          await updateOptIn.mutateAsync({
            resourceIds: {
              id: selectedOptIn.optInId,
            },
            data: data,
          });
        }
        onApply(mergeRight(fields, selectedOptIn));
      } else {
        onCancel();
      }
    } catch (err) {
      setErrors(set(lensProp("main"), err));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle id="form-dialog-title">{`${
        notBlank(selectedOptIn) ? "Edit" : "Add"
      } Project Opt-In`}</DialogTitle>
      {errors.main && (
        <Grid container justifyContent="center">
          <ErrorMessage error={errors.main} />
        </Grid>
      )}
      <DialogContent>
        {updateOptIn.isPending && <LoadingIndicator transparent />}
        {optInsResponse.isLoading && <Skeleton variant="text" />}
        {allTrue([optInsResponse.isSuccess, notBlank(optIns)]) && (
          <Fragment>
            {isBlank(selectedOptIn) && (
              <DialogContentText>
                Select an Opt-In to add to this Project.
              </DialogContentText>
            )}
            <Grid container spacing={4}>
              <Grid item xs={12}>
                {notBlank(selectedOptIn) ? (
                  <OptInForm
                    optIn={editableFields}
                    errors={errors}
                    handleBlur={handleBlur}
                    handleChange={handleChange}
                    handleCheckbox={handleCheckbox}
                  />
                ) : allTrue([optInsResponse.isSuccess, notBlank(optIns)]) ? (
                  <Autocomplete
                    id="opt-in"
                    options={caseInsensitiveSort("optInName")(optIns)}
                    getOptionLabel={option =>
                      notBlank(option) && `${option.optInName}`
                    }
                    isOptionEqualToValue={(opt, val) =>
                      notBlank(val) && equals(opt.optInId, val.optInId)
                    }
                    value={selectedOptIn}
                    filterSelectedOptions
                    onChange={handleOptInChange}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Opt-In"
                        placeholder="Select..."
                      />
                    )}
                  />
                ) : (
                  <Skeleton variant="text" />
                )}
              </Grid>
              <Grid item sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={fields.preSubmission}
                      onChange={handleCheckboxChange("preSubmission")}
                      name="pre-submission"
                      inputProps={{ "aria-label": "Select" }}
                    />
                  }
                  label="Pre-Submission"
                />
              </Grid>
              <Grid item sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={fields.required}
                      onChange={handleCheckboxChange("required")}
                      name="required"
                      inputProps={{ "aria-label": "Select" }}
                    />
                  }
                  label="Required"
                />
              </Grid>
            </Grid>
          </Fragment>
        )}
        {optInsResponse.isSuccess && isBlank(optIns) && (
          <DialogContentText>
            There are no Opt-Ins available to add to this Project.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectOptInDialog;
